/* global StorageEvent */
/** Вкладка «Подбор поставки» (`tab=draft`) — по образцу «Распределения»: строка — товар (оффер
 *  кабинета), раскрывается до его регионов WB (`/offers`, строки `wb_region_now` как есть); регионы
 *  назначения — галочками «Куда планируем» (draftTargets.ts), товары отмечаются галочкой, количество
 *  вводится у региона. Итог — Excel (`POST /supply-draft.xlsx`, строки товар × включённый регион с
 *  qty > 0) по показанным или отмеченным показанным товарам; не фиксация. Черновик (отметки и
 *  количества) и цели — в localStorage браузера (draftStorage.ts, draftTargets.ts); не прочитался /
 *  не сохраняется — пометка на экране, вкладка работает и без него. «Заполнить до N дней» —
 *  временное согласованное исключение из правила «расчёты только в dwh» (формула — в подсказке
 *  кнопки и в supplyDraft.ts; «в пути» не учтено). Квант — из 1С (`GET /in-box`, как у
 *  «Распределения»); «Округлять до кванта» (draftQuant.ts) переключает итог региона и выгрузку
 *  между «Нужно» и «С квантом»; квант не загружен — выгрузка с округлением закрыта текстом. */
import type { ExpandedState } from "@tanstack/react-table";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { exportWbSupplyDraft, type WbOffers } from "@/api/wbRegions";
import { extractErrorMessage } from "@/lib/api-errors";
import { saveBlob } from "@/lib/download";
import { cn } from "@/lib/utils";
import { int } from "../ozon-analytics/format";
import { apiErrorText } from "../ozon-analytics/ui";
import { loadRound, quantIndex, ROUND_HINT, ROUND_STORAGE_KEY, saveRound } from "./draftQuant";
import { DraftTable } from "./DraftTable";
import { DRAFT_STORAGE_KEY, EMPTY_DRAFT, isEmptyDraft, loadDraft, saveDraft, type Draft } from "./draftStorage";
import { loadTargets, regionOptions, saveTargets, TARGETS_STORAGE_KEY, type Targets } from "./draftTargets";
import {
  buildProducts,
  exportProducts,
  exportRows,
  fillTarget,
  fillToDays,
  filterProducts,
  setLeafQty,
  summarize,
  toggleSel,
  type ExportMode,
  type FillResult,
} from "./draftTree";
import { useWbInBox } from "./queries";
import { RESET_DRAFT_FILTERS, type DraftParams, type Patch } from "./state";
import { DEFAULT_FILL_DAYS, FILL_FORMULA, MAX_QTY, parseDays } from "./supplyDraft";
import { TargetsPopover } from "./TargetsPopover";
import { useDebouncedText } from "./useDebouncedText";

const RED_BOX =
  "rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-400";
const AMBER_BOX =
  "rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300";
const BTN = "h-8 rounded-md border bg-background px-3 text-xs hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50";

function SearchBox({ value, onChange }: { value: string; onChange: (v: string) => void }): React.ReactElement {
  const [draft, setDraft] = useDebouncedText(value, onChange);
  return (
    <input
      type="search"
      aria-label="Поиск оффера"
      placeholder="Артикул, nm, название"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      className="h-8 w-64 rounded-md border bg-background px-2 text-xs"
    />
  );
}

/** 400 с ошибкой валидации полей строки (лишнее / отсутствующее поле): вкладка старше бэкенда. */
function isStaleFormat(e: unknown): boolean {
  const r = (e as { response?: { status?: number } })?.response;
  if (r?.status !== 400) return false;
  return /Extra inputs are not permitted|Field required/.test(extractErrorMessage(e, ""));
}

function fillText(r: FillResult, days: number, who: string): string {
  const parts = [
    `до ${days} дн. (${who}): заполнено регионов ${r.filled}`,
    `вышло 0 — ${r.zero}`,
    `пропущено (нет спроса или остатка, количество очищено) — ${r.skipped}`,
  ];
  if (r.overLimit) parts.push(`превышает лимит ${int(MAX_QTY)} (количество очищено) — ${r.overLimit}`);
  if (r.noKey) parts.push(`без offer_key — ${r.noKey}`);
  return parts.join(" · ");
}

/** Состояние, которое пишется в localStorage и перечитывается по `storage` другой вкладки:
 *  последняя запись выигрывает, в памяти — всегда актуальная копия. */
function useStored<T>(
  key: string,
  load: () => { value: T; error: string | null },
  save: (v: T) => string | null,
): {
  value: T;
  update: (f: (v: T) => T) => void;
  loadError: string | null;
  saveError: string | null;
  synced: boolean;
} {
  const [initial] = useState(load);
  const [value, setValue] = useState<T>(initial.value);
  const [loadError, setLoadError] = useState<string | null>(initial.error);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [synced, setSynced] = useState(false);
  const dirty = useRef(false);
  const fromStorage = useRef(false);
  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    if (fromStorage.current) {
      // состояние только что прочитано из storage — писать его обратно незачем
      fromStorage.current = false;
      return;
    }
    if (!dirty.current) return;
    setSaveError(save(value));
  }, [value, save]);

  useEffect(() => {
    const onStorage = (e: StorageEvent): void => {
      if (e.key !== null && e.key !== key) return;
      const fresh = load();
      // тот же объект (EMPTY_DRAFT / DEFAULT_TARGETS) эффект записи не запустит — флаг застрял бы и съел следующую правку
      fromStorage.current = fresh.value !== valueRef.current;
      setValue(fresh.value);
      setLoadError(fresh.error);
      setSynced(true);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [key, load]);

  const update = useCallback((f: (v: T) => T) => {
    dirty.current = true;
    setValue(f);
  }, []);
  return { value, update, loadError, saveError, synced };
}

// модульные (стабильные по ссылке): useStored держит их в зависимостях эффектов
const loadDraftValue = (): { value: Draft; error: string | null } => {
  const r = loadDraft();
  return { value: r.draft, error: r.error };
};
const saveDraftValue = (v: Draft): string | null => saveDraft(v);
const loadTargetsValue = (): { value: Targets; error: string | null } => {
  const r = loadTargets();
  return { value: r.targets, error: r.error };
};
const saveTargetsValue = (v: Targets): string | null => saveTargets(v);
const loadRoundValue = (): { value: boolean; error: string | null } => {
  const r = loadRound();
  return { value: r.on, error: r.error };
};
const saveRoundValue = (v: boolean): string | null => saveRound(v);

export function SupplyDraftTab({
  data,
  params,
  q,
  deficitOnly,
  onPatch,
}: {
  data: WbOffers;
  params: DraftParams;
  q: string;
  deficitOnly: boolean;
  onPatch: (p: Patch) => void;
}): React.ReactElement {
  const d = useStored(DRAFT_STORAGE_KEY, loadDraftValue, saveDraftValue);
  const t = useStored(TARGETS_STORAGE_KEY, loadTargetsValue, saveTargetsValue);
  const rb = useStored(ROUND_STORAGE_KEY, loadRoundValue, saveRoundValue);
  const inBox = useWbInBox();
  const draft = d.value;
  const targets = t.value;
  const round = rb.value;
  const updateDraft = d.update;
  const updateTargets = t.update;
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [daysText, setDaysText] = useState(String(DEFAULT_FILL_DAYS));
  const [fill, setFill] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [exportError, setExportError] = useState<unknown>(null);
  const [exportNote, setExportNote] = useState<{ text: string; empty: boolean } | null>(null);

  const onQty = useCallback(
    (key: string, v: number | null) => updateDraft((x) => ({ ...x, qty: setLeafQty(x.qty, key, v) })),
    [updateDraft],
  );
  const onToggleSel = useCallback((key: string) => updateDraft((x) => ({ ...x, sel: toggleSel(x.sel, key) })), [updateDraft]);
  const onTargets = useCallback((x: Targets) => updateTargets(() => x), [updateTargets]);
  const onShowOff = useCallback((v: boolean) => onPatch({ off: v ? "1" : null }), [onPatch]);

  const regions = useMemo(() => regionOptions(data.offers), [data.offers]);
  // ошибка повторного запроса — старые data не берём: плашка говорит «не загружен», считать надо так же
  const quants = useMemo(() => (inBox.data && !inBox.isError ? quantIndex(inBox.data) : null), [inBox.data, inBox.isError]);
  const products = useMemo(() => buildProducts(data.offers, targets, quants), [data.offers, targets, quants]);
  const shown = useMemo(
    () => filterProducts(products, { q, deficitOnly, markedOnly: params.markedOnly }, draft.sel),
    [products, q, deficitOnly, params.markedOnly, draft.sel],
  );
  const sum = useMemo(() => summarize(products, shown, draft, round), [products, shown, draft, round]);
  const days = parseDays(daysText);
  const target = fillTarget(shown, draft.sel);
  const filtered = Boolean(q || deficitOnly || params.markedOnly);
  const markedHidden = sum.marked - sum.markedShown;

  const onFill = (): void => {
    if (days === null) return;
    const r = fillToDays(target.products, draft.qty, days);
    updateDraft((x) => ({ ...x, qty: r.qty }));
    setFill(fillText(r, days, target.marked ? "отмеченные показанные товары" : "показанные товары"));
  };

  const onClear = (): void => {
    if (!window.confirm("Очистить черновик поставки (все отметки и количества во всех кабинетах)?")) return;
    updateDraft(() => EMPTY_DRAFT);
    setFill(null);
  };

  /** Выгрузка как `fixScope` «Распределения»: `shown` — показанные товары, `selected` —
   *  отмеченные И показанные; строки — включённые регионы с количеством > 0. Пусто — текст, не файл. */
  const onExport = async (mode: ExportMode): Promise<void> => {
    const ps = exportProducts(shown, draft.sel, mode);
    const rows = exportRows(ps, draft.qty, round);
    setExportError(null);
    if (round && quants === null) {
      // без кванта «С квантом» = «Нужно» у всех — молча выгрузить неокруглённое нельзя
      setExportNote({
        empty: true,
        text: "Квант из 1С не загружен — выгрузить с округлением нельзя. Повторите загрузку кванта или снимите «Округлять до кванта»",
      });
      return;
    }
    if (rows.length === 0) {
      setExportNote({
        empty: true,
        text:
          mode === "selected"
            ? "У отмеченных показанных товаров нет количества > 0 во включённых регионах — выгружать нечего"
            : "У показанных товаров нет количества > 0 во включённых регионах — выгружать нечего",
      });
      return;
    }
    const withQty = ps.filter((p) => exportRows([p], draft.qty, round).length > 0).length;
    setBusy(true);
    setExportNote(null);
    try {
      const { blob, filename } = await exportWbSupplyDraft(data.state.built_at, rows);
      saveBlob(blob, filename);
      const parts = [`выгружено строк: ${rows.length} (товаров: ${withQty})`];
      if (quants === null) parts.push("квант из 1С не загружен — колонка «Квант» пустая");
      else if (round) {
        const unrounded = rows.filter((r) => r.in_box === null).length;
        if (unrounded > 0) parts.push(`не округлено строк: ${unrounded} (квант не задан / конфликт / нет nm)`);
      }
      if (mode === "selected" && ps.length > withQty) {
        parts.push(`у ${ps.length - withQty} отмеченных товаров нет количества во включённых регионах — не вошли`);
      }
      if (mode === "selected" && markedHidden > 0) parts.push(`ещё ${markedHidden} отмеченных скрыто фильтром — не вошли`);
      setExportNote({ empty: false, text: parts.join(" · ") });
    } catch (e) {
      setExportError(e);
    } finally {
      setBusy(false);
    }
  };

  const banners = [
    { id: "draft-load-error", text: d.loadError, alert: true },
    { id: "draft-targets-load-error", text: t.loadError, alert: true },
    { id: "draft-synced", text: d.synced || t.synced || rb.synced ? "черновик/настройки обновлены в другой вкладке" : null, alert: false },
    { id: "draft-save-error", text: d.saveError, alert: true },
    { id: "draft-targets-save-error", text: t.saveError, alert: true },
    { id: "draft-round-load-error", text: rb.loadError, alert: true },
    { id: "draft-round-save-error", text: rb.saveError, alert: true },
  ];

  return (
    <div className="space-y-3" data-testid="supply-draft">
      {banners.map(
        (b) =>
          b.text && (
            <div key={b.id} role={b.alert ? "alert" : "status"} className={AMBER_BOX} data-testid={b.id}>
              {b.text}
            </div>
          ),
      )}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-md border bg-muted/30 px-3 py-2 text-sm" data-testid="draft-summary">
        <span>
          Показанные: Σ <b>{int(sum.shownQty)}</b> шт.
        </span>
        <span>
          Отмечено товаров: <b>{sum.marked}</b>, из них показано {sum.markedShown} (Σ {int(sum.markedQty)} шт. по показанным)
        </span>
        {sum.byRegion.length > 0 && (
          <span className="text-xs text-muted-foreground" data-testid="draft-by-region">
            по регионам: {sum.byRegion.map((g) => `${g.region} ${int(g.qty)} шт. (${g.rows})`).join(" · ")}
          </span>
        )}
        {sum.outside > 0 && (
          <span className="text-xs text-amber-700 dark:text-amber-400" data-testid="draft-outside">
            ещё {sum.outside} отмеченных товаров нет в данных вкладки (другой кабинет или пропали из витрины) — не
            выгружаются
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <button type="button" className={BTN} onClick={onClear} disabled={isEmptyDraft(draft)}>
            Очистить
          </button>
          <button
            type="button"
            className={cn(BTN, "border-primary")}
            onClick={() => void onExport("shown")}
            disabled={busy || shown.length === 0}
            title="В Excel — показанные товары (после фильтров) × включённые регионы с количеством > 0"
          >
            {busy ? "Выгружаю…" : "Выгрузить показанные"}
          </button>
          <button
            type="button"
            className={cn(BTN, "border-primary")}
            onClick={() => void onExport("selected")}
            disabled={busy || sum.markedShown === 0}
            title={
              "В Excel — отмеченные показанные товары × включённые регионы с количеством > 0" +
              (markedHidden > 0 ? `; ещё ${markedHidden} отмеченных скрыто фильтром и НЕ уйдёт` : "")
            }
          >
            Выгрузить отмеченные ({sum.markedShown})
          </button>
        </div>
      </div>
      {exportNote && (
        <div
          role={exportNote.empty ? "alert" : "status"}
          className={exportNote.empty ? AMBER_BOX : "text-xs text-muted-foreground"}
          data-testid={exportNote.empty ? "draft-export-empty" : "draft-export-done"}
        >
          {exportNote.text}
        </div>
      )}
      {exportError !== null && (
        <div role="alert" className={RED_BOX} data-testid="draft-export-error">
          Не удалось выгрузить Excel. {apiErrorText(exportError, "«WB: регионы»")}
          {isStaleFormat(exportError) && " — обновите страницу (вкладка могла открыться до обновления портала)"}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
        <SearchBox value={q} onChange={(v) => onPatch({ q: v || null })} />
        <label className="flex items-center gap-1.5">
          <input type="checkbox" checked={deficitOnly} onChange={(e) => onPatch({ def: e.target.checked ? "1" : null })} />
          Только дефицит (дефицит / мало во включённом регионе)
        </label>
        <label className="flex items-center gap-1.5">
          <input type="checkbox" checked={params.markedOnly} onChange={(e) => onPatch({ marked: e.target.checked ? "1" : null })} />
          Только отмеченные
        </label>
        <TargetsPopover regions={regions} targets={targets} onChange={onTargets} showOff={params.showOff} onShowOff={onShowOff} />
        <button type="button" className={BTN} onClick={() => setExpanded(true)} disabled={shown.length === 0}>
          Раскрыть все
        </button>
        <button type="button" className={BTN} onClick={() => setExpanded({})}>
          Свернуть
        </button>
        <span className="text-muted-foreground" data-testid="draft-count">
          товаров: {shown.length} из {products.length}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs">
        <label className="flex items-center gap-1.5">
          Заполнить до
          <input
            type="text"
            inputMode="numeric"
            aria-label="Дней покрытия для заполнения"
            value={daysText}
            onChange={(e) => setDaysText(e.target.value)}
            className={cn("h-8 w-14 rounded-md border bg-background px-2 text-right text-xs", days === null && "border-red-400")}
          />
          дней
        </label>
        <button
          type="button"
          className={BTN}
          onClick={onFill}
          disabled={days === null || target.products.length === 0}
          title={`${FILL_FORMULA}\n\nСейчас применится к: ${target.marked ? "отмеченным показанным" : "показанным"} товарам (${target.products.length}).`}
          data-testid="draft-fill"
        >
          Заполнить до {days ?? "N"} дней — {target.marked ? "отмеченные" : "показанные"} ({target.products.length})
        </button>
        <span className="cursor-help text-muted-foreground" title={FILL_FORMULA}>
          ⌈спрос FBO/день × N − остаток FBO⌉ по включённым регионам, в пути не учтено ⓘ
        </span>
        {days === null && <span className="text-red-600 dark:text-red-400">N — целое от 1 до 365</span>}
        {fill && (
          <span className="text-muted-foreground" data-testid="draft-fill-result">
            {fill}
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs" data-testid="draft-quant-panel">
        <label className="flex items-center gap-1.5" title={ROUND_HINT}>
          <input type="checkbox" checked={round} onChange={(e) => rb.update(() => e.target.checked)} />
          Округлять до кванта (вверх) ⓘ
        </label>
        {inBox.isPending && <span className="text-muted-foreground">квант: загружаю из 1С…</span>}
        {inBox.data && !inBox.isError && (
          <span
            className={cn(round && sum.quantUnset + sum.quantConflict > 0 ? "text-amber-700 dark:text-amber-400" : "text-muted-foreground")}
            title={`${inBox.data.source}. Без кванта и с конфликтом — не округляются (пометка ⚠ в «С квантом»).`}
            data-testid="draft-quant-counts"
          >
            среди показанных: квант не задан — {sum.quantUnset} тов. · конфликт квантов — {sum.quantConflict} тов.
          </span>
        )}
      </div>
      {inBox.isError && (
        <div role="alert" className={RED_BOX} data-testid="draft-quant-error">
          Квант из 1С не загружен — «С квантом» не считается. {apiErrorText(inBox.error, "WB")}{" "}
          <button type="button" className="underline" onClick={() => void inBox.refetch()}>
            Повторить
          </button>
        </div>
      )}

      {shown.length === 0 ? (
        <div className="rounded-md border bg-muted/30 px-4 py-6 text-sm" data-testid="draft-empty">
          {filtered ? (
            <>
              Ничего не найдено.{" "}
              <button type="button" className="underline" onClick={() => onPatch(RESET_DRAFT_FILTERS)}>
                Сбросить фильтры
              </button>
            </>
          ) : (
            "У кабинета нет строк в витрине «сейчас»."
          )}
        </div>
      ) : (
        <DraftTable
          shown={shown}
          draft={draft}
          round={round}
          showOff={params.showOff}
          expanded={expanded}
          onExpandedChange={setExpanded}
          onQty={onQty}
          onToggleSel={onToggleSel}
        />
      )}
    </div>
  );
}
